import { fetcherFunction } from 'lib-knowledge-utils/restClient';
import type {
  ReadSearchApiResponse,
  SearchParameters,
  SearchScope,
} from 'lib-search-types';
import { useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';

type SearchResource<TScope extends SearchScope> =
  ReadSearchApiResponse<TScope>['data']['results'][number]['result'];

type UseSearchFetchResult<TScope extends SearchScope> = {
  data: SearchResource<TScope>[];
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: () => void;
  prevPage: () => void;
  error?: Error;
};

type UseSearchFetchOptions = {
  skip: boolean;
};

export function useSearchFetch<TScope extends SearchScope>(
  arg: Omit<SearchParameters<TScope>, 'scope'> & { scope: TScope },
  { skip }: UseSearchFetchOptions,
): UseSearchFetchResult<TScope> {
  const { scope, ...rest } = arg;
  const path = `v4/search/${scope}`;

  const { data, isLoading, isValidating, size, setSize, error } =
    useSWRInfinite<ReadSearchApiResponse<TScope>>(
      (pageIndex) => (skip ? null : [path, { ...rest, page: pageIndex }]),
      ([, body]) =>
        fetcherFunction<ReadSearchApiResponse<TScope>>(
          { path, method: 'POST' },
          { arg: { body } },
        ),
      { revalidateFirstPage: false },
    );

  const lastPage = data?.at(-1);
  const totalPages = lastPage?.meta.total_pages ?? 0;

  const nextPage = useCallback(() => {
    setSize((s) => s + 1);
  }, [setSize]);

  const prevPage = useCallback(() => {
    setSize((s) => Math.max(1, s - 1));
  }, [setSize]);

  return {
    data:
      data?.flatMap((page) => page.data.results.map(({ result }) => result)) ??
      [],
    isLoading,
    isFetching: isValidating,
    error,
    hasNextPage: size < totalPages,
    hasPrevPage: size > 1,
    nextPage,
    prevPage,
  };
}
